import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';
import { clearEnforcement, enforcementCopy, readEnforcement } from '../lib/enforcement';
import { formatDateTime } from '../lib/utils';

const SUPPORT_EMAIL = 'i@kol.my';

export default function AccountSuspended() {
  const navigate = useNavigate();
  const [info] = useState(() => readEnforcement());
  const copy = enforcementCopy(info?.code);
  const code = info?.code ?? 'ACCESS_RESTRICTED';
  const untilDate = info?.until ? new Date(info.until) : null;
  const untilLabel = untilDate && !Number.isNaN(untilDate.getTime()) ? formatDateTime(untilDate) : null;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Account access (${code})`)}`;

  const tryAgain = () => {
    clearEnforcement();
    navigate('/login');
  };

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-8 pt-24 text-center font-sans">
        <div className="mx-auto max-w-2xl">
          <div className="mb-8 flex justify-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <ShieldOff className="h-12 w-12 text-rose-400" aria-hidden />
            </div>
          </div>

          <h1 className="mb-4 bg-gradient-to-r from-rose-400 to-orange-300 bg-clip-text text-4xl font-bold text-transparent md:text-5xl">
            {copy.title}
          </h1>
          <p className="mb-8 text-lg text-gray-400">{copy.body}</p>

          {(info?.reason || untilLabel) && (
            <div className="mb-8 rounded-xl border border-white/10 bg-white/5 p-5 text-left">
              {info?.reason && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Reason given</div>
                  <p className="mt-1 whitespace-pre-wrap text-gray-200">{info.reason}</p>
                </div>
              )}
              {untilLabel && (
                <div className={info?.reason ? 'mt-4' : ''}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Until</div>
                  <p className="mt-1 text-gray-200">{untilLabel}</p>
                </div>
              )}
            </div>
          )}

          <div className="mb-12 flex flex-wrap justify-center gap-4">
            <a
              href={mailto}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-8 py-4 text-lg font-semibold text-white transition-all hover:shadow-xl hover:shadow-violet-500/30"
            >
              Contact {SUPPORT_EMAIL}
            </a>
            <Link
              to="/"
              className="rounded-lg border border-white/10 bg-white/5 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white/10"
            >
              Back to home
            </Link>
            <button
              type="button"
              onClick={tryAgain}
              className="rounded-lg border border-white/10 bg-white/5 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white/10"
            >
              Try signing in again
            </button>
          </div>

          <div className="text-sm text-gray-500">
            <p>Error Code: {code}</p>
            <p>Timestamp: {new Date().toISOString()}</p>
          </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
}
